import {
  Body1,
  Body2,
  Button,
  Caption1,
  Card,
  CardHeader,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Divider,
  Link,
  Subtitle1,
  Subtitle2,
  Title1,
} from '@fluentui/react-components';
import {
  bundleIcon,
  ArrowRight16Filled,
  ArrowRight16Regular,
  Beaker24Filled,
  Beaker24Regular,
  DataBarVertical24Filled,
  DataBarVertical24Regular,
  DataPie24Filled,
  DataPie24Regular,
  Database24Filled,
  Database24Regular,
  Dismiss20Regular,
  Gavel24Filled,
  Gavel24Regular,
  QuestionCircle20Regular,
  Sparkle24Filled,
  Sparkle24Regular,
} from '@fluentui/react-icons';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import {
  DATASET_ENTITLEMENT_NAME_PROD,
  DATASET_ENTITLEMENT_NAME_TEST,
  DATASET_ENTITLEMENT_URL_PROD,
  DATASET_ENTITLEMENT_URL_TEST,
} from '../../constants/index.js';
import { useWorkspaceContext } from '../../context/WorkspaceContext.js';
import { getJudgeFactoryPath } from '../../features/judge-factory/index.js';

const ArrowRightIcon = bundleIcon(
  ArrowRight16Filled,
  ArrowRight16Regular,
) as React.FC;
const DatasetIcon = bundleIcon(Database24Filled, Database24Regular) as React.FC;
const JudgeIcon = bundleIcon(Gavel24Filled, Gavel24Regular) as React.FC;
const EvalIcon = bundleIcon(Beaker24Filled, Beaker24Regular) as React.FC;
const MonitorIcon = bundleIcon(DataPie24Filled, DataPie24Regular) as React.FC;
const MetricsIcon = bundleIcon(
  DataBarVertical24Filled,
  DataBarVertical24Regular,
) as React.FC;
const SyntheticIcon = bundleIcon(Sparkle24Filled, Sparkle24Regular) as React.FC;

import { useStyles } from './HomePage.style';

const PLATFORM_NODES = [
  { title: 'Dataset Hub', sub: 'Golden datasets', icon: DatasetIcon },
  { title: 'Judge Factory', sub: 'Prompt synthesis', icon: JudgeIcon },
  { title: 'Graders', sub: 'Quality scoring', icon: MetricsIcon },
  { title: 'Evaluation Engine', sub: 'Offline & online', icon: EvalIcon },
  { title: 'Metrics & Reports', sub: 'Continuous insight', icon: MetricsIcon },
  { title: 'Monitoring', sub: 'Production tracking', icon: MonitorIcon },
] as const;

interface GettingStartedInfo {
  steps: string[];
  permissions: string[];
  prerequisites: React.ReactNode[];
}

const SUITE_CARDS: readonly {
  title: string;
  description: string;
  action: string;
  route?: string;
  href?: string;
  icon: React.FC;
  gettingStarted?: GettingStartedInfo;
}[] = [
  {
    title: 'Datalake',
    description:
      'Curate golden datasets, production traces, and schema-aligned mappings.',
    action: 'Open Datasets',
    route: '/datasets',
    icon: DatasetIcon,
    gettingStarted: {
      steps: [
        'Create a new dataset and define its schema',
        'Import entries from CSV or add them manually',
        'Tag and version your dataset for reproducibility',
        'Share the dataset with your workspace collaborators',
      ],
      permissions: [
        'Workspace Contributor or Owner role',
        'Datalake read/write access for dataset storage',
      ],
      prerequisites: [
        <>
          Request access to test environment:{' '}
          <a
            href={DATASET_ENTITLEMENT_URL_TEST}
            target="_blank"
            rel="noreferrer"
          >
            {DATASET_ENTITLEMENT_NAME_TEST}
          </a>
        </>,
        <>
          Request access to prod environment:{' '}
          <a
            href={DATASET_ENTITLEMENT_URL_PROD}
            target="_blank"
            rel="noreferrer"
          >
            {DATASET_ENTITLEMENT_NAME_PROD}
          </a>
        </>,
      ],
    },
  },
  {
    title: 'Synthetic Data Generation',
    description:
      'Generate synthetic datasets to bootstrap evaluations when real data is scarce.',
    action: 'Generate Synthetic Data',
    route: '/datasets?openSynthetic=true',
    icon: SyntheticIcon,
    gettingStarted: {
      steps: [
        'Click on "New Dataset" in the Datalake page',
        'Select "Synthetic Data" as the data source',
        'Configure generation parameters and run the job',
        'Review the generated data for quality',
        'Upload to Datalake if the generated data looks good',
      ],
      permissions: ['Datalake read/write access'],
      prerequisites: [],
    },
  },
  {
    title: 'Evaluation Tenants',
    description:
      'Use the official Teams test tenant and find guidance for tenant data, access, changes, and recreation.',
    action: 'Open Tenant Guidance',
    route: '/evaluation-tenants',
    icon: SyntheticIcon,
  },
  {
    title: 'Judge Factory',
    description:
      'Generate tone, safety, and task judges with iterative prompt refinement.',
    action: 'Open Judges',
    route: '__judge-factory__',
    icon: JudgeIcon,
    gettingStarted: {
      steps: [
        'Describe the quality dimension you want to evaluate',
        'Upload a golden dataset with example scores',
        'Review generated judge variants and select the best',
        'Publish the judge for use in evaluations',
      ],
      permissions: ['Workspace Contributor or Owner role'],
      prerequisites: [
        'A workspace has been created and selected',
        'A golden dataset with labeled examples (CSV or JSON)',
      ],
    },
  },
  {
    title: 'Evaluation Studio',
    description:
      'Create eval criteria, schemas, and quality guardrails for every judge run.',
    action: 'Open Studio',
    route: '__evaluation-studio__',
    icon: EvalIcon,
    gettingStarted: {
      steps: [
        'Create an evaluation template with pass/fail criteria',
        'Define quality signals and scoring rubrics',
        'Configure agents and graders for your evaluation',
        'Run your first evaluation against a dataset',
      ],
      permissions: [
        'Workspace Contributor or Owner role',
        'Access to Datalake',
      ],
      prerequisites: [
        'A workspace has been created and selected',
        'At least one dataset is available in the Dataset Hub',
      ],
    },
  },
  {
    title: 'Eval Runs',
    description:
      'Track evaluation run results, compare agent performance, and monitor quality metrics.',
    action: 'View Eval Runs',
    route: '__eval-runs__',
    icon: MetricsIcon,
    gettingStarted: {
      steps: [
        'Select a workspace to view evaluation runs',
        'Filter runs by status, agent, or grader',
        'Click a completed run to view its report',
        'Compare results across runs to track improvements',
      ],
      permissions: [
        'Workspace Contributor or Owner role',
        'Read access to evaluation run results',
      ],
      prerequisites: [
        'A workspace has been created and selected',
        'At least one evaluation has been run from a template',
      ],
    },
  },
];

export function HomePage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { activeName: workspaceName } = useWorkspaceContext();
  const [activeGuide, setActiveGuide] = useState<GettingStartedInfo | null>(
    null,
  );
  const [activeGuideTitle, setActiveGuideTitle] = useState('');

  const resolveRoute = (route: string) => {
    const workspace = workspaceName ?? 'public';

    switch (route) {
      case '__judge-factory__':
        return getJudgeFactoryPath(workspace);
      case '__evaluation-studio__':
        return `/workspaces/${encodeURIComponent(workspace)}/evaluation-templates`;
      case '__eval-runs__':
        return `/workspaces/${encodeURIComponent(workspace)}/eval-runs`;
      default:
        return route;
    }
  };

  const openGuide = (title: string, info: GettingStartedInfo) => {
    setActiveGuideTitle(title);
    setActiveGuide(info);
  };

  const closeGuide = () => {
    setActiveGuide(null);
    setActiveGuideTitle('');
  };

  return (
    <div className={styles.root}>
      {/* ── Hero ── */}
      <section className={styles.hero}>
        <div className={styles.heroTopBorder} aria-hidden="true" />
        <div className={styles.heroBottomBorder} aria-hidden="true" />
        <div className={styles.heroDotGrid} aria-hidden="true" />
        <div className={styles.heroOrbLeft} aria-hidden="true" />
        <div className={styles.heroOrbRight} aria-hidden="true" />
        <div className={styles.heroContent}>
          <Title1 className={styles.heroTitle}>
            Evaluate, ship, and{' '}
            <span className={styles.heroTitleAccent}>continuously improve</span>{' '}
            AI systems
          </Title1>
          <Body1 className={styles.heroSubtitle}>
            A trusted workflow for offline, online, and production evaluation.
            Build judges, run experiments, monitor quality, and close the
            feedback loop — all from one platform.
          </Body1>
        </div>
      </section>

      {/* ── Evaluation suite ── */}
      <section className={`${styles.section} ${styles.suiteSectionAnim}`}>
        <div className={styles.sectionHeader}>
          <Subtitle1>Powerful Evaluation Suite</Subtitle1>
          <Body1 className={styles.sectionSub}>
            Purpose-built tools that work together across the evaluation
            lifecycle.
          </Body1>
        </div>
        <div className={styles.suiteGrid}>
          {SUITE_CARDS.map((card, i) => {
            const Icon = card.icon;
            const gettingStarted = card.gettingStarted;
            return (
              <Card
                key={card.title}
                className={styles.suiteCard}
                style={{
                  animationName: 'aiFadeSlideUp',
                  animationDuration: '0.45s',
                  animationDelay: `${0.3 + i * 0.06}s`,
                  animationTimingFunction: 'cubic-bezier(0.16,1,0.3,1)',
                  animationFillMode: 'both',
                }}
              >
                <CardHeader
                  image={
                    <div className={styles.suiteIconWrap}>
                      <Icon />
                    </div>
                  }
                  header={
                    <Subtitle2 className={styles.suiteTitle}>
                      {card.title}
                    </Subtitle2>
                  }
                  action={
                    gettingStarted ? (
                      <Button
                        appearance="transparent"
                        size="small"
                        icon={<QuestionCircle20Regular />}
                        aria-label={`Getting started with ${card.title}`}
                        title="Getting started"
                        onClick={() => openGuide(card.title, gettingStarted)}
                      />
                    ) : undefined
                  }
                />
                <Caption1 className={styles.suiteDesc}>
                  {card.description}
                </Caption1>
                {card.href ? (
                  <Link
                    className={styles.suiteActionLink}
                    href={card.href}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {card.action} →
                  </Link>
                ) : (
                  <Link
                    className={styles.suiteActionLink}
                    onClick={() => {
                      if (card.route) {
                        void navigate(resolveRoute(card.route));
                      }
                    }}
                  >
                    {card.action} →
                  </Link>
                )}
              </Card>
            );
          })}
        </div>
      </section>

      <Divider className={styles.divider} />

      {/* ── Platform pipeline ── */}
      <section className={`${styles.section} ${styles.pipelineSectionAnim}`}>
        <div className={styles.sectionHeader}>
          <Subtitle1>One Platform, End-to-End</Subtitle1>
          <Body1 className={styles.sectionSub}>
            Every component connected — from data ingestion to production
            insight.
          </Body1>
        </div>
        <Card className={styles.pipelineCard}>
          <div className={styles.pipelineRow}>
            {PLATFORM_NODES.map((node, index) => {
              const Icon = node.icon;
              return (
                <div key={node.title} className={styles.pipelineItem}>
                  <div className={styles.pipelineIconBadge}>
                    <Icon />
                  </div>
                  <span className={styles.pipelineTitle}>{node.title}</span>
                  <span className={styles.pipelineSub}>{node.sub}</span>
                  {index < PLATFORM_NODES.length - 1 && (
                    <span className={styles.pipelineConnector}>
                      <ArrowRightIcon />
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      </section>

      {/* Getting started modal */}
      <Dialog
        open={activeGuide !== null}
        onOpenChange={(_e, data) => {
          if (!data.open) closeGuide();
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button
                    appearance="subtle"
                    aria-label="Close"
                    icon={<Dismiss20Regular />}
                  />
                </DialogTrigger>
              }
            >
              Getting Started — {activeGuideTitle}
            </DialogTitle>
            <DialogContent className={styles.guideContent}>
              <div className={styles.guideSection}>
                <Body2 className={styles.guideSectionTitle}>Steps</Body2>
                <ol className={styles.guideList}>
                  {activeGuide?.steps.map((step) => (
                    <li key={step}>
                      <Body1>{step}</Body1>
                    </li>
                  ))}
                </ol>
              </div>

              <div className={styles.guideSection}>
                <Body2 className={styles.guideSectionTitle}>
                  Required Permissions
                </Body2>
                <ul className={styles.guideList}>
                  {activeGuide?.permissions.map((perm) => (
                    <li key={perm}>
                      <Body1>{perm}</Body1>
                    </li>
                  ))}
                </ul>
              </div>

              {activeGuide && activeGuide.prerequisites.length > 0 && (
                <div className={styles.guideSection}>
                  <Body2 className={styles.guideSectionTitle}>
                    Prerequisites
                  </Body2>
                  <ul className={styles.guideList}>
                    {activeGuide.prerequisites.map((prereq, idx) => (
                      <li key={idx}>
                        <Body1>{prereq}</Body1>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
